<template>
  <q-dialog v-model="showModal" persistent>
    <q-card style="max-width: 400px; width: 90%;">
      <q-card-section class="text-center">
        <q-icon name="cloud_download" size="48px" color="primary" class="q-mb-md" />
        <div class="text-h6 q-mb-sm">Update Available</div>
        <p class="text-grey-7 q-mb-md">
          A new version of the application is available. Please refresh to continue using the app.
        </p>
        <div class="bg-grey-2 rounded-borders q-pa-sm q-mb-lg">
          <span class="text-grey-7 text-body2">New version: </span>
          <span class="text-weight-bold" style="font-family: monospace;">{{ serverVersion }}</span>
        </div>
        <q-btn
          color="primary"
          label="Refresh Now"
          class="full-width"
          size="lg"
          @click="refresh"
        />
      </q-card-section>
    </q-card>
  </q-dialog>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue';

const VERSION_KEY = 'app_version';
const CHECK_INTERVAL = 5 * 60 * 1000; // 5 minutes

const showModal = ref(false);
const serverVersion = ref('');
let checkInterval: ReturnType<typeof setInterval> | null = null;

async function fetchVersion(): Promise<string | null> {
  try {
    const response = await fetch('/api/version');
    if (!response.ok) {
      console.warn('Version check failed:', response.status);
      return null;
    }
    const data = await response.json();
    return data.version;
  } catch (error) {
    console.warn('Version check error:', error);
    return null;
  }
}

async function checkVersion() {
  const newVersion = await fetchVersion();
  if (!newVersion) return;

  const storedVersion = localStorage.getItem(VERSION_KEY);

  if (!storedVersion) {
    // First visit - store the version
    localStorage.setItem(VERSION_KEY, newVersion);
    return;
  }

  if (storedVersion !== newVersion) {
    // Version mismatch - show blocking modal
    serverVersion.value = newVersion;
    showModal.value = true;
  }
}

async function refresh() {
  // Update stored version before refresh
  if (serverVersion.value) {
    localStorage.setItem(VERSION_KEY, serverVersion.value);
  }

  // Clear service worker caches
  if ('serviceWorker' in navigator && 'caches' in window) {
    try {
      const cacheNames = await caches.keys();
      await Promise.all(cacheNames.map(name => caches.delete(name)));
    } catch (error) {
      console.warn('Failed to clear caches:', error);
    }
  }

  // Unregister service workers
  if ('serviceWorker' in navigator) {
    try {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(registrations.map(reg => reg.unregister()));
    } catch (error) {
      console.warn('Failed to unregister service workers:', error);
    }
  }

  // Hard reload
  window.location.reload();
}

onMounted(() => {
  // Check version on mount
  checkVersion();

  // Check every 5 minutes
  checkInterval = setInterval(checkVersion, CHECK_INTERVAL);
});

onUnmounted(() => {
  if (checkInterval) {
    clearInterval(checkInterval);
  }
});
</script>
